import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
export function Landing() {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isShaking, setIsShaking] = useState(false);
  const navigate = useNavigate();
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim().toLowerCase() === 'demo99') {
      sessionStorage.setItem('bibde_access', 'granted');
      navigate('/dashboard/overview');
    } else {
      setError('Invalid access code. Try again.');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    }
  };
  const scrollToAccess = () => {
    document.getElementById('access-form')?.focus();
  };
  const stagger = 0.1;
  return <div className="min-h-screen bg-[var(--bg)] text-[var(--fg)] overflow-x-hidden font-body flex flex-col" data-id="element-405">
      {/* Top Bar */}
      <header className="h-20 border-b border-white/5 flex items-center justify-between px-6 md:px-12 shrink-0" data-id="element-406">
        <div className="flex items-center gap-3" data-id="element-407">
          <div className="w-8 h-8 bg-[var(--accent)] rounded-lg flex items-center justify-center" data-id="element-408">
            <div className="w-3 h-3 bg-black rounded-sm" data-id="element-409" />
          </div>
          <span className="font-display font-bold text-2xl tracking-wide text-white" data-id="element-410">
            BIBDE
          </span>
        </div>
        <button onClick={scrollToAccess} className="text-sm font-medium text-[var(--muted)] hover:text-white transition-colors" data-id="element-411">
          Enter access code
        </button>
      </header>

      <main className="flex-1" data-id="element-412">
        {/* Hero Section */}
        <section className="px-6 md:px-12 py-20 md:py-32 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-413">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="space-y-8" data-id="element-414">
            <h1 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl leading-tight text-white" data-id="element-415">
              The AI Operating System for{' '}
              <span className="text-[var(--accent)]" data-id="element-416">Event Workforce</span>
            </h1>

            {/* Access Code Form */}
            <div className="bg-[var(--surface)] border border-white/5 rounded-2xl p-6 md:p-8 max-w-md shadow-2xl relative overflow-hidden" data-id="element-417">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--accent)]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-418" />
              <h2 className="text-lg font-display font-bold text-white mb-2 relative z-10" data-id="element-419">
                Access the Demo
              </h2>
              <p className="text-sm text-[var(--muted)] mb-6 relative z-10" data-id="element-420">
                Enter your invitation code to explore the platform.
              </p>

              <form onSubmit={handleSubmit} className="relative z-10 space-y-4" data-id="element-421">
                <div data-id="element-422">
                  <label htmlFor="access-form" className="sr-only" data-id="element-423">
                    Access Code
                  </label>
                  <motion.div animate={isShaking ? {
                  x: [-10, 10, -10, 10, 0]
                } : {}} transition={{
                  duration: 0.4
                }} data-id="element-424">
                    <input id="access-form" type="text" value={code} onChange={e => {
                    setCode(e.target.value);
                    setError('');
                  }} placeholder="Enter access code" className={`w-full bg-[var(--bg)] border ${error ? 'border-[var(--danger)]' : 'border-white/10 focus:border-[var(--accent)]/50'} rounded-lg px-4 py-3 text-white focus:outline-none transition-colors placeholder:text-[var(--muted)]`} aria-invalid={!!error} aria-describedby={error ? 'code-error' : undefined} data-id="element-425" />
                  </motion.div>
                  {error && <p id="code-error" className="text-[var(--danger)] text-sm mt-2 font-medium flex items-center gap-1.5" data-id="element-426">
                      {error}
                    </p>}
                </div>
                <button type="submit" className="w-full bg-[var(--accent)] hover:bg-[#b3e622] text-black px-4 py-3 rounded-lg font-bold transition-colors flex items-center justify-center gap-2" data-id="element-427">
                  Launch App
                  <ArrowRight className="w-4 h-4" data-id="element-428" />
                </button>
                <p className="text-xs text-[var(--muted)] text-center pt-2" data-id="element-429">
                  Hint: Demo code is{' '}
                  <span className="font-mono text-white/70" data-id="element-430">demo99</span>
                </p>
              </form>
            </div>
          </motion.div>

          {/* Decorative Mockup */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="hidden lg:block relative" data-id="element-431">
            <div className="absolute inset-0 bg-gradient-to-tr from-[var(--accent)]/20 to-transparent rounded-3xl blur-3xl" data-id="element-432" />
            <div className="relative space-y-4" data-id="element-433">
              {/* Fake Stat Cards */}
              <div className="grid grid-cols-2 gap-4" data-id="element-434">
                <div className="bg-[var(--surface)] border border-white/5 rounded-xl p-6 shadow-xl" data-id="element-435">
                  <span className="text-xs font-semibold text-[var(--muted)] tracking-wider mb-2 uppercase block" data-id="element-436">
                    Total Required
                  </span>
                  <span className="text-4xl font-display font-bold text-white" data-id="element-437">
                    450
                  </span>
                </div>
                <div className="bg-[var(--surface)] border border-white/5 rounded-xl p-6 shadow-xl" data-id="element-438">
                  <span className="text-xs font-semibold text-[var(--muted)] tracking-wider mb-2 uppercase block" data-id="element-439">
                    Confirmed
                  </span>
                  <span className="text-4xl font-display font-bold text-[var(--success)]" data-id="element-440">
                    442
                  </span>
                </div>
              </div>
              {/* Fake Smart Assignments */}
              <div className="bg-[var(--surface)] border border-white/5 rounded-xl overflow-hidden shadow-xl" data-id="element-441">
                <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between bg-white/[0.02]" data-id="element-442">
                  <h2 className="text-sm font-medium text-white" data-id="element-443">
                    Smart Assignments
                  </h2>
                  <div className="flex items-center gap-1.5 text-[var(--accent)] text-xs font-medium bg-[var(--accent)]/10 px-2.5 py-1 rounded-full" data-id="element-444">
                    <CheckCircle2 className="w-3.5 h-3.5" data-id="element-445" />
                    Optimized
                  </div>
                </div>
                <div className="divide-y divide-white/5" data-id="element-446">
                  {[{
                  station: 'Water Station 1',
                  time: '06:00 - 10:00',
                  filled: '12/12',
                  status: 'Ready'
                }, {
                  station: 'Finish Line Medical',
                  time: '05:30 - 14:00',
                  filled: '8/8',
                  status: 'Ready'
                }, {
                  station: 'Registration Desk',
                  time: '04:00 - 10:00',
                  filled: '24/25',
                  status: 'Pending'
                }].map((row, i) => <div key={i} className="px-6 py-4 flex items-center justify-between" data-id="element-447">
                      <span className="text-sm font-medium text-white" data-id="element-448">
                        {row.station}
                      </span>
                      <span className="text-sm text-[var(--muted)]" data-id="element-449">
                        {row.filled}
                      </span>
                      <span className={`text-sm font-medium ${row.status === 'Ready' ? 'text-[var(--success)]' : 'text-orange-400'}`} data-id="element-450">
                        {row.status}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Stats Section */}
        <section className="py-24" data-id="element-451">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-452">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center" data-id="element-453">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} data-id="element-454">
                <div className="font-display font-bold text-6xl text-white mb-2" data-id="element-455">
                  98<span className="text-[var(--accent)]" data-id="element-456">%</span>
                </div>
                <div className="text-lg text-[var(--muted)] font-medium" data-id="element-457">
                  Average Fill Rate
                </div>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.1
            }} data-id="element-458">
                <div className="font-display font-bold text-6xl text-white mb-2" data-id="element-459">
                  3<span className="text-[var(--accent)]" data-id="element-460">hrs</span>
                </div>
                <div className="text-lg text-[var(--muted)] font-medium" data-id="element-461">
                  Planning Time (Down from 30 days)
                </div>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} data-id="element-462">
                <div className="font-display font-bold text-6xl text-white mb-2" data-id="element-463">
                  10<span className="text-[var(--accent)]" data-id="element-464">k+</span>
                </div>
                <div className="text-lg text-[var(--muted)] font-medium" data-id="element-465">
                  Verified Talent Profiles
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 mt-auto" data-id="element-466">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-467">
          <div className="flex items-center gap-2" data-id="element-468">
            <div className="w-5 h-5 bg-[var(--accent)] rounded flex items-center justify-center" data-id="element-469">
              <div className="w-1.5 h-1.5 bg-black rounded-sm" data-id="element-470" />
            </div>
            <span className="font-display font-bold tracking-wide text-white" data-id="element-471">
              BIBDE
            </span>
          </div>
          <p className="text-sm text-[var(--muted)]" data-id="element-472">
            &copy; {new Date().getFullYear()} Bibde Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}